import { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '../../components/toast/ToastContext';
import DeletePrompt from '../../components/DeletePrompt';
import { useNavigate } from 'react-router-dom';
import { ActionType, Column, MetadataField } from '../../types/types';
import {
  useGetDynamicColumnsQuery,
  useGetDynamicFiltersQuery,
  useLazyGetOrganizationsQuery,
  useDeleteOrganizationMutation
} from '../../api/organizationApiSlice';
import AMSDynamicListing from '../../components/listing/AMSDynamicListing';
import { getAccessToken } from '../auth/services/auth';
import { useGetAllDynamicColumnsQuery } from '../../api/contactApiSlice';
import { useBulkDeleteMutation } from '../../api/searchQueryApiSlice';

interface OrganizationsListingProps {
  onEditOrganization: (id: number, onSuccess: () => void) => void;
  refreshTrigger?: number;
}

interface SavedTableParams {
  page: number;
  perPage: number;
  sort?: { field: string; direction: 'ASC' | 'DESC' };
  query?: string;
  filters: Record<string, any>;
  visibleColumns: string[];
}

const OrganizationsListing = ({ onEditOrganization, refreshTrigger }: OrganizationsListingProps) => {
  const STORAGE_KEY = `tableConfig_organizations`;
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filterGroups, setFilterGroups] = useState<any[]>([]);
  const initialMount = useRef(true);
  const showToast = useToast();
  const navigate = useNavigate();
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [columns, setColumns] = useState<Column[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteIds, setDeleteIds] = useState<number[]>([]);
  const [loadingExport, setLoadingExport] = useState(false);
  const [initialFilters, setInitialFilters] = useState<SavedTableParams | null>(null);
  const [pendingCleanup, setPendingCleanup] = useState<(() => void) | null>(null);

  const [triggerFetchOrganizations, { data, isLoading, isFetching }] = useLazyGetOrganizationsQuery();
  const [deleteOrganization] = useDeleteOrganizationMutation();
  const isFetchComplete = !isLoading && !isFetching && data !== undefined;

  // const { data: dynamicColsData, isLoading: isDynamicColsLoading } = useGetDynamicColumnsQuery('organization',);
  const { data: dynamicColsData, isLoading: isDynamicColsLoading } = useGetAllDynamicColumnsQuery({ scope: "organization", isStandard: false });
  const { data: dynamicFiltersData, isLoading: isDynamicFiltersLoading } = useGetDynamicFiltersQuery('organization',);
  const [bulkDelete, { isLoading: bulkDeleteLoading }] =
    useBulkDeleteMutation();
  const getSavedParams = useCallback((): SavedTableParams | null => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;

    try {
      const parsed = JSON.parse(saved);
      if (parsed &&
        typeof parsed.page === 'number' &&
        typeof parsed.perPage === 'number' &&
        (parsed.sort === undefined || (
          typeof parsed.sort?.field === 'string' &&
          ['ASC', 'DESC'].includes(parsed.sort?.direction)
        )) &&
        (parsed.query === undefined || typeof parsed.query === 'string') &&
        parsed.filters && typeof parsed.filters === 'object' &&
        Array.isArray(parsed.visibleColumns)
      ) {
        return {
          page: parsed.page,
          perPage: parsed.perPage,
          sort: parsed.sort,
          query: parsed.query || '',
          filters: parsed.filters || {},
          visibleColumns: parsed.visibleColumns || []
        };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const fetchOrganizations = useCallback(async (
    params: SavedTableParams,
    onError?: (error: any) => void
  ) => {
    try {
      const result = await triggerFetchOrganizations({
        page: params.page,
        perPage: params.perPage,
        sort: params.sort,
        query: params.query,
        filters: params.filters,
        visibleColumns: params.visibleColumns,
      }).unwrap();
      return result;
    } catch (err) {
      showToast('Fetch Failed', 'Failed to fetch organizations. Please try again.', 'error');
      if (onError) onError(err);
      return null;
    }
  }, [triggerFetchOrganizations]);

  const saveParams = useCallback((params: SavedTableParams) => {
    const existing = localStorage.getItem(STORAGE_KEY);
    let merged = params;
    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        merged = { ...parsed, ...params };
      } catch {}
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  }, []);

  useEffect(() => {
    if (data) {
      setOrganizations(data.results ?? []);
      setTotalItems(data.total ?? 0);
    }
  }, [data]);
  useEffect(() => {
    if (dynamicColsData) {
      const transformed = dynamicColsData.map((col: any) => ({
        id: col.field || col.id || '',
        label: col.label || '',
        isSortable: col.isSortable ?? true,
      }));
      const idAddedCols = [
        { id: 'organization_id', label: 'organization_id', isSortable: false },
        ...transformed
      ];
      setColumns(idAddedCols);

      const savedParams = getSavedParams();
      const defaultParams: SavedTableParams = {
        page: 1,
        perPage: 10,
        filters: {},
        visibleColumns: idAddedCols
          .filter((col: any) => col.id?.startsWith('o.') || col.id === 'organization_id')
          .splice(0, 4)
          .map((col: any) => col.id),
        query: "",
      };

      // Intersect saved visibleColumns with current columns to avoid restoring invalid ids
      const currentColumnIds = new Set(idAddedCols.map(c => c.id));

      let initialParams: SavedTableParams;
      if (savedParams) {
        const restoredVisible = (savedParams.visibleColumns || []).filter((c: string) => currentColumnIds.has(c));
        initialParams = {
          ...defaultParams,
          ...savedParams,
          visibleColumns: restoredVisible.length > 0 ? restoredVisible : defaultParams.visibleColumns,
        };
        if (JSON.stringify(restoredVisible) !== JSON.stringify(savedParams.visibleColumns)) {
          saveParams(initialParams);
        }
      } else {
        initialParams = defaultParams;
        saveParams(initialParams);
      }

      setInitialFilters(initialParams);

      // Fetch organizations immediately after setting up columns and initial filters
      fetchOrganizations(initialParams);
    }
  }, [dynamicColsData, getSavedParams, saveParams, fetchOrganizations]);

  useEffect(() => {
    if (dynamicFiltersData?.length) {
      const fieldsToExclude = ['age', 'diabetes'];
      const filtered = dynamicFiltersData.filter(
        (item: MetadataField) =>
          !fieldsToExclude.some((excluded) =>
            item.field.toLowerCase().includes(excluded.toLowerCase())
          )
      );

      const transformed = filtered.map((col: any) => ({
        key: col.field || '',
        label: col.label || '',
        dataType: col.dataType || '',
        options: col.options ?? [],
      }));

      setFilterGroups(transformed);
    }
  }, [dynamicFiltersData]);

  useEffect(() => {
    if (refreshTrigger && initialFilters) {
      fetchOrganizations(initialFilters);
    }
  }, [refreshTrigger, initialFilters, fetchOrganizations]);

  const handleFetchRequested = useCallback((params: SavedTableParams) => {
    saveParams(params);
    setInitialFilters(params);
    if (!params.visibleColumns.length) return;
    fetchOrganizations(params);
  }, [fetchOrganizations, saveParams]);

  // const confirmDelete = async () => {
  //   if (!deleteId) return;
  //   const numericId = Number(deleteId);
  //   setShowDeletePrompt(false);

  //   try {
  //     await deleteOrganization(numericId).unwrap();
  //     setOrganizations(prev => prev.filter(org => org.id !== numericId));
  //     setTotalItems(prev => prev - 1);
  //     showToast('Organization Deleted', 'The selected organization has been deleted.', 'success');

  //     const savedParams = getSavedParams();
  //     if (savedParams) {
  //       fetchOrganizations(savedParams);
  //     }
  //     if (pendingCleanup) {
  //       pendingCleanup();
  //       setPendingCleanup(null);
  //     }
  //   } catch {
  //     showToast('Delete Failed', 'Failed to delete organization. Please try again.', 'error');
  //   }

  //   setDeleteId(null);
  // };



  const confirmDelete = async () => {
      // Stop if neither a deleteId nor selectedIds exist
      if (!deleteId && (!deleteIds || deleteIds.length === 0)) return;
  
      setShowDeletePrompt(false);
  
      try {
        if (deleteIds && deleteIds.length > 0) {
          // 🧩 Bulk delete case
          const response = await bulkDelete({
            recordIds: deleteIds,
            scope: 'organization',
          }).unwrap();
  
          setOrganizations(prev =>
            prev.filter(org => !deleteIds.includes(org?.id))
          );
          setTotalItems(prev => prev - deleteIds?.length);
        } else if (deleteId) {
          // 🧩 Single delete case
          const numericId = Number(deleteId);
          await deleteOrganization(numericId).unwrap();
  
          setOrganizations(prev =>
            prev.filter(org => org.id !== numericId)
          );
          setTotalItems(prev => prev - 1);
        }
  
        // ✅ Refresh and notify
        showToast('Organization Deleted', 'The selected organization has been deleted.', 'success');
        const savedParams = getSavedParams();
        if (savedParams) fetchOrganizations(savedParams);
        if (pendingCleanup) {
          pendingCleanup();
          setPendingCleanup(null);
        }
      } catch (error) {
        console.error('Delete failed:', error);
        showToast('Delete Failed', 'Failed to delete organization. Please try again.', 'error');
      } finally {
        // ✅ Always clear delete state
        setDeleteId(null);
        setDeleteIds([]);
      }
    };



  const cancelDelete = () => {
    setShowDeletePrompt(false);
    setDeleteId(null);
  };

  const handleAction = useCallback(async (action: ActionType, id: string | number[], state?: string | (() => void)) => {
    const numericId = Number(id);

    // if (action === 'delete') {
    //   setDeleteId(numericId);
    //   setPendingCleanup(() => state);
    //   setShowDeletePrompt(true);
    //   return;
    // }
    if (action === 'delete') {
      if (Array.isArray(id) && id.length > 0) {
        setDeleteIds(id);
      } else {
        setDeleteId(numericId);
      }
      setPendingCleanup(() => state);
      setShowDeletePrompt(true);
      return;
    }


    if (action === 'edit') {
      onEditOrganization(numericId, () => {
        const savedParams = getSavedParams();
        if (savedParams) {
          fetchOrganizations(savedParams);
        }
      });
    }

    if (action === 'view') {
      navigate(`${numericId}`);
    }
  }, [fetchOrganizations, onEditOrganization, navigate, getSavedParams]);
  const exportFiles = async (entity: string) => {
    setLoadingExport(true);
    const savedParams = getSavedParams();
    try {
      const token = getAccessToken(); // adjust if token is from Redux or cookies
      const queryString = new URLSearchParams();
      queryString.append('export', '1');
      if (savedParams?.visibleColumns?.length) {
        let columns = savedParams?.visibleColumns
          .filter((column: any) => column !== 'organization_id')
          .join(',');
        queryString.append('listingColumns', columns);
      }
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/${entity}?${queryString.toString()}`;
      const response = await fetch(apiUrl,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        showToast("Error", "Failed to export file", "error");
        return;
      }

      const blob = await response.blob();

      // Extract filename from content-disposition header (optional)
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
      const filename =
        filenameMatch?.[1] ||
        `export-${entity}.xlsx`;

      // Trigger file download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showToast("Success", "File downloaded successfully", "success");
    } catch (err) {
      console.error(err);
      showToast("Error", "Something went wrong while exporting", "error");
    } finally {
      setLoadingExport(false);
    }
  };
  return (
    <>
      <AMSDynamicListing
        showFilters
        showDateRangeFilter
        showActions
        routeKey="organizations"
        noDataTitle="No Organizations Found"
        noDataSubtitle="Your organization list is currently empty. Start adding new organizations to stay organized."
        filterGroups={filterGroups}
        title="Organizations Listing"
        subtitle="All organizations will show here"
        columns={columns}
        data={organizations}
        totalItems={totalItems}
        loading={isLoading || isDynamicColsLoading || isDynamicFiltersLoading}
        isFetching={isFetching || isDynamicFiltersLoading || isDynamicColsLoading}
        isFetchComplete={isFetchComplete}
        onAction={handleAction}
        onFetchRequested={handleFetchRequested}
        noTableAction={true}
        setInitialFilters={setInitialFilters}
        dynamicColumnsListing={true}
        skeletonHeight={'20px'}
        onExport={() => exportFiles('organizations')}
        loadingExport={loadingExport}
        hideActionColumn={true}
      />
      {showDeletePrompt && (
        <DeletePrompt onConfirm={confirmDelete} onCancel={cancelDelete} />
      )}
    </>
  );
};

export default OrganizationsListing;